/**
 * Comprehensive hook for widget editing
 * Combines config, editing, saving, and theme access into a single hook
 * Reduces repeated boilerplate across all widget components
 */

import { useState, useCallback, useMemo } from 'react';
import { EditField } from '../components/EditPanel/types';
import { hasEditableThemeTarget, useWidgetThemeParams } from '../utils/useWidgetThemeParams';
import { updateWidgetConfig as apiUpdateWidgetConfig, ThemeFontValue, ThemeIconValue } from '../api/widgetThemeApi';
import {
  useWidgetConfig,
  useWidgetEdit,
  useWidgetTheme,
  useThemeColors,
  useThemeFonts,
  useThemeIcons,
} from './useWidgetTheme';
import { createResolveColor, ResolveColorFn } from './useColorResolver';

interface UseWidgetEditableReturn<T> {
  // State
  state: T;
  isLoading: boolean;
  isSaving: boolean;
  // Edit handlers
  handleEditChange: (key: string, value: any) => void;
  handleSave: () => Promise<void>;
  /** Reset a single field to its component default (removes the saved override) */
  resetField: (key: string) => void;
  /** Set of field keys that have saved (non-default) values in the backend */
  savedKeys: Set<string>;
  /** Set of field keys locked by Concierge Studio */
  studioLockedKeys: Set<string>;
  // Theme assets
  themeColors: Record<string, string>;
  themeFonts: Record<string, ThemeFontValue>;
  themeIcons: Record<string, ThemeIconValue>;
  // Color resolution
  resolveColor: ResolveColorFn;
  // Utility
  canSave: boolean;
  getEditValues: () => Record<string, any>;
}

/**
 * Hook that provides all common widget editing functionality
 *
 * @param widgetId - Unique identifier for the widget instance
 * @param defaultState - Default state values for the widget
 * @param editFields - Field definitions for the edit panel (used for auto-transformations)
 * @returns Object with state, handlers, and theme assets
 */
export function useWidgetEditable<T extends Record<string, any>>(
  widgetId: string | undefined,
  defaultState: T,
  editFields: EditField[],
): UseWidgetEditableReturn<T> {
  // Get widget config from cache
  const { isLoading, savedKeys, ...savedState } = useWidgetConfig<T>(widgetId, defaultState);
  const state = savedState as unknown as T;

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Theme resources
  const { themeColors } = useThemeColors();
  const { themeFonts } = useThemeFonts();
  const { themeIcons } = useThemeIcons();

  // Create a bound resolveColor function
  const resolveColor = useMemo(() => createResolveColor(themeColors), [themeColors]);

  // Read studioControlledKeys from the theme response to lock fields
  const { data: themeData } = useWidgetTheme();
  const studioControlledWidgetKeys = useMemo(() => {
    const sck = (themeData as any)?.studioControlledKeys?.widgetConfig;
    if (!sck || !widgetId) return new Set<string>();
    return new Set<string>(sck[widgetId] || []);
  }, [themeData, widgetId]);

  // Theme params for saving
  const themeParams = useWidgetThemeParams();

  // Widget edit functions
  const { updateWidgetConfig: updateConfig, removeWidgetConfigKey, getWidgetState } = useWidgetEdit(widgetId);

  const resetField = useCallback(
    (key: string) => {
      removeWidgetConfigKey(key);
    },
    [removeWidgetConfigKey],
  );

  // Create a map of field keys to their types for quick lookup
  // Include mobile_ variants for responsive fields
  const fieldTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    editFields.forEach((field) => {
      map[field.key] = field.type;
      // For responsive fields, the mobile_ variant has the same type
      if (field.responsive) {
        map[`mobile_${field.key}`] = field.type;
      }
    });
    return map;
  }, [editFields]);

  // Handle edit changes with auto-transformations based on field type
  const handleEditChange = useCallback(
    (key: string, value: any) => {
      if (studioControlledWidgetKeys.has(key)) return;

      const fieldType = fieldTypeMap[key];
      let transformedValue = value;

      // Auto-transform based on field type
      switch (fieldType) {
        case 'toggle':
          // Convert string "true"/"false" to boolean
          if (typeof value === 'string') {
            transformedValue = value === 'true';
          }
          break;
        case 'number':
          // Convert string to number if needed
          if (typeof value === 'string' && value !== '') {
            const parsed = parseFloat(value);
            if (!isNaN(parsed)) {
              transformedValue = parsed;
            }
          }
          break;
        case 'options':
          if (typeof value === 'string') {
            // Convert "true"/"false" strings to boolean
            if (value === 'true') {
              transformedValue = true;
            } else if (value === 'false') {
              transformedValue = false;
            }
            // Convert numeric strings (e.g., font weights "400", "500") to numbers
            else if (/^\d+$/.test(value)) {
              transformedValue = parseInt(value, 10);
            }
          }
          break;
        default:
          // No transformation needed
          break;
      }

      updateConfig(key, transformedValue);
    },
    [fieldTypeMap, updateConfig],
  );

  // V1 needs themeId; V2 needs appId+scope+version (see hasEditableThemeTarget).
  const canSave = Boolean(hasEditableThemeTarget(themeParams) && widgetId);

  // Handle save with error alert
  const handleSave = useCallback(async () => {
    if (!hasEditableThemeTarget(themeParams) || !widgetId || !themeParams.editThemeToken) {
      console.warn('Cannot save: missing editable theme target or editThemeToken');
      return;
    }

    setIsSaving(true);
    try {
      const currentState = getWidgetState() || state;
      await apiUpdateWidgetConfig({
        // V2 write path ignores themeId and uses getV2EditTarget() from the URL.
        themeId: themeParams.themeId || '',
        widgetId: widgetId,
        widgetState: currentState,
        editThemeToken: themeParams.editThemeToken,
      });
    } catch (error: any) {
      console.error('Failed to save widget config:', error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  }, [themeParams, widgetId, getWidgetState, state]);

  // Get edit values with reverse transformations for display
  // (e.g., booleans/numbers back to strings for options fields)
  const getEditValues = useCallback(() => {
    const values: Record<string, any> = { ...state };

    editFields.forEach((field) => {
      const value = values[field.key];

      if (field.type === 'options') {
        // Convert boolean to "true"/"false" string
        if (typeof value === 'boolean') {
          values[field.key] = value ? 'true' : 'false';
        }
        // Convert number to string (for font weights etc.)
        else if (typeof value === 'number') {
          values[field.key] = String(value);
        }
      }
    });

    return values;
  }, [state, editFields]);

  return {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    resetField,
    savedKeys,
    studioLockedKeys: studioControlledWidgetKeys,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
    getEditValues,
  };
}
